import { useMemo, useState } from 'react';
import classNames from 'clsx';
import { diffJson } from 'diff';

import { groupChangesForCollapse } from './diffCollapse';
import { renderCollapsibleDiff, renderDiff } from './unifiedDiffRenderer';
import { diffByLine, diffJSONByLine, stringifyForDiff } from './utils';

import styles from './Diff.module.css';

export type DiffViewProps = {
  original: string;
  compare: string;
  showLineNumbers?: boolean;
  includeSubDiffs?: boolean;
  title?: string;
  isJSON?: boolean;
  className?: string;
  contextLines?: number;
};

export { stringifyForDiff };

export function Diff({
  original,
  compare,
  showLineNumbers = true,
  title,
  includeSubDiffs = false,
  isJSON = false,
  className,
  contextLines = 0,
}: DiffViewProps) {
  const isSameDocument = useMemo(() => original === compare, [original, compare]);

  const diff = useMemo(() => {
    if (isJSON) {
      if (showLineNumbers) {
        return diffJSONByLine(original, compare);
      }

      return diffJson(original, compare);
    }

    return diffByLine(original, compare);
  }, [original, compare, isJSON, showLineNumbers]);

  const groups = useMemo(() => {
    return contextLines ? groupChangesForCollapse(diff, contextLines) : [];
  }, [diff, contextLines]);

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const handleToggleExpand = (groupIndex: number) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupIndex)) {
        newSet.delete(groupIndex);
      } else {
        newSet.add(groupIndex);
      }
      return newSet;
    });
  };

  const shouldRenderGroupedDiff = contextLines > 0;

  return (
    <div className={classNames(styles.diffView, { [styles.diffViewSameDoc]: isSameDocument })}>
      {title && (
        <div className={styles.controls}>
          <div className={styles.title}>{title}</div>
        </div>
      )}
      <div className={classNames(styles.diff, className)} data-test-id="diff-container">
        {shouldRenderGroupedDiff
          ? renderCollapsibleDiff(
              groups,
              expandedGroups,
              showLineNumbers,
              includeSubDiffs,
              contextLines,
              handleToggleExpand,
            )
          : renderDiff(diff, showLineNumbers, includeSubDiffs)}
      </div>
    </div>
  );
}
